import React, { useState, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';

/**
 * SimpleChatThread - Unified chat component for both client and staff
 * @param {string} clientId - The client ID
 * @param {string} mode - "client" or "staff" (determines who is "me")
 * @param {string} backendUrl - Backend API URL
 */
const SimpleChatThread = ({ clientId, mode, backendUrl }) => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const chatContainerRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/clients/${clientId}/messages`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const markAsRead = async () => {
    try {
      await fetch(`${backendUrl}/api/clients/${clientId}/messages/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ who: mode })
      });
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  const sendMessage = async () => {
    if (!draft.trim() || sending) return;

    setSending(true);
    try {
      const res = await fetch(`${backendUrl}/api/clients/${clientId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from: mode === 'client' ? 'client' : 'staff',
          body: draft.trim()
        })
      });

      if (res.ok) {
        setDraft('');
        await fetchMessages();
        // Auto-scroll will happen via useEffect when messages update
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchMessages();
      markAsRead();

      // Poll every 7 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchMessages();
      }, 7000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [clientId, mode]);

  // Auto-scroll ONLY when messages change (new messages added)
  useEffect(() => {
    if (!chatContainerRef.current) return;
    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }, [messages]);

  const isMine = (msg) => {
    if (mode === 'client') return msg.from === 'client';
    if (mode === 'staff') return msg.from === 'staff';
    return false;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="chat-thread-container">
      <div className="chat-thread" ref={chatContainerRef}>
        {messages.map((m) => {
          const mine = isMine(m);
          const system = m.from === 'system';

          if (system) {
            return (
              <div key={m.id} className="msg-system">
                <span>{m.body}</span>
                <span className="ts">{formatDateTime(m.created_at)}</span>
              </div>
            );
          }

          return (
            <div key={m.id} className={`msg ${mine ? 'mine' : 'other'}`}>
              <div className="bubble">
                <p>{m.body}</p>
                <span className="ts">{formatDateTime(m.created_at)}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="chat-input-wrapper">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your message..."
          disabled={sending}
          rows="2"
        />
        <button onClick={sendMessage} disabled={sending || !draft.trim()}>
          <Send size={18} />
        </button>
      </div>

      <div className="chat-hint">
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>
  );
};

export default SimpleChatThread;
